import React, { useMemo, useState, useEffect } from "react";
import {
  Paper,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@material-ui/core";
import { stableSort, getComparator } from "App/Component/ui/datatable/utils";

import {
  DatatablesFooter,
  IconButtonDatatable,
  ModalDeleteConfirm,
  SearchInput,
  TableHeader,
  TableToolbar,
  Title,
} from "App/Component/ui";
import {
  deleteJenisLayananById,
  FormJenisLayanan,
  getAllJenisLayanan,
  headCells,
  RESET_STATE_JENIS_LAYANAN,
  STYLE_JENIS_LAYANAN,
} from "./ui";
import { useSearchDatatables, useWindowDimensions } from "App/hooks";
import {
  formatNumber,
  defaultDateDisplay,
  formatRupiah,
  scrollToTop,
} from "App/utils/helpers";
import { PencilIcon, TrashIcon } from "App/Assets";
import { MINUS_WIDTH } from "App/constants";

let PageSize = 10;

export default function JenisLayanan() {
  const classes = STYLE_JENIS_LAYANAN();
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PageSize);

  const { width } = useWindowDimensions();

  const [showModalDeleteConfirm, setShowModalDeleteConfirm] = useState(false);

  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);

  const [formData, setFormData] = useState(RESET_STATE_JENIS_LAYANAN);

  const [run, setRun] = useState(false);
  const { data, length } = useSearchDatatables(rows, q, [
    "name",
    "kode_bridge",
  ]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  useEffect(() => {
    (async () => {
      const res = await getAllJenisLayanan();
      if (res) {
        setRows(res);
      }
    })();
  }, [run]);

  const setVal = (row) => {
    setFormData({
      id: row.id,
      name: row.name,
      harga: 0,
      harga2: row.harga === null ? "0" : formatRupiah(row.harga.toString()),
      typeofcare_code: row.typeofcare_code,
      remark: row.remark,
      kode_bridge: row.kode_bridge,
    });
  };

  useMemo(() => {
    const firstPageIndex = (page - 1) * PageSize;
    const lastPageIndex = firstPageIndex + PageSize;
    return data.slice(firstPageIndex, lastPageIndex);
  }, [page, data]);

  return (
    <div
      style={{
        maxWidth: `${width - MINUS_WIDTH}px`,
      }}>
      {showModalDeleteConfirm && (
        <ModalDeleteConfirm
          confirmHandle={() =>
            deleteJenisLayananById(
              formData.id,
              () => setRun(!run),
              setShowModalDeleteConfirm,
              setFormData
            )
          }
          setShowModalDeleteConfirm={() => setShowModalDeleteConfirm(false)}
        />
      )}

      <Title title={"Jenis Layanan"} />

      <FormJenisLayanan
        formData={formData}
        setFormData={setFormData}
        run={() => setRun(!run)}
      />

      <div className='mt-2'>
        <Paper className={classes.paper}>
          <TableToolbar title={"Daftar Jenis Layanan"}>
            <SearchInput
              withIcon={true}
              onchange={(e) => setQ(e.target.value)}
            />
          </TableToolbar>
          <TableContainer className='custom-scrollbar'>
            <Table
              className={classes.table}
              aria-labelledby='tableTitle'
              size={"medium"}
              aria-label='enhanced table'>
              <TableHeader
                classes={classes}
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                rowCount={length}
                headCells={headCells}
              />
              <TableBody>
                {stableSort(data, getComparator(order, orderBy))
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => {
                    return (
                      <TableRow
                        hover
                        tabIndex={-1}
                        key={index}
                        style={{
                          backgroundColor:
                            index % 2 === 0 ? "transparent" : "#fff",
                        }}>
                        <TableCell
                          component='th'
                          scope='row'
                          padding='none'
                          align='center'>
                          {index + 1}
                        </TableCell>

                        <TableCell
                          component='th'
                          scope='row'
                          padding='none'
                          align='center'>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "7px",
                            }}>
                            <IconButtonDatatable
                              onClick={() => {
                                setVal(row);
                                scrollToTop();
                              }}>
                              <PencilIcon />
                            </IconButtonDatatable>

                            {row?.typeofcare_code === "RJALAN" && (
                              <IconButtonDatatable
                                onClick={() => {
                                  setVal(row);
                                  setShowModalDeleteConfirm(
                                    !showModalDeleteConfirm
                                  );
                                }}>
                                <TrashIcon />
                              </IconButtonDatatable>
                            )}
                          </div>
                        </TableCell>
                        <TableCell align='left'>{row?.id}</TableCell>
                        <TableCell align='left'>{row?.name}</TableCell>
                        <TableCell align='left'>{row?.kode_bridge}</TableCell>
                        <TableCell align='left'>
                          {formatNumber(row?.harga)}
                        </TableCell>
                        <TableCell align='left'>
                          {row?.typeofcare_code}
                        </TableCell>
                        <TableCell align='left'>{row?.remark}</TableCell>

                        <TableCell align='left'>
                          {defaultDateDisplay(row?.createdAt)}
                        </TableCell>
                        <TableCell align='left'>{row?.createdBy}</TableCell>
                        <TableCell align='left'>
                          {defaultDateDisplay(row?.updatedAt)}
                        </TableCell>
                        <TableCell align='left'>{row?.updatedBy}</TableCell>
                      </TableRow>
                    );
                  })}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell align='center' colSpan={headCells.length}>
                      Tidak ada data
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <DatatablesFooter
            page={page}
            length={length}
            rowsPerPage={rowsPerPage}
            selectRows={[10, 20]}
            onPageChange={(page) => setPage(page - 1)}
            onSelectChange={(e) => setRowsPerPage(e.target.value)}
          />
        </Paper>
      </div>
    </div>
  );
}
